import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";

// --- User bubble (split) ---

export const userBubbleRootVariants = cva(
  "tw-relative tw-isolate tw-max-w-60 tw-w-fit tw-ml-auto tw-py-2.5 tw-px-3.5 tw-text-[0.875rem]/5 tw-font-medium",
  {
    variants: {
      variant: {
        outline: "tw-text-white",
        elevated: "tw-text-black",
        cream: "tw-text-black",
        gray: "tw-text-gray-500",
        glass: "tw-text-white",
      },
    },
    defaultVariants: { variant: "outline" },
  },
);

export const userBubbleBackgroundVariants = cva("tw-absolute tw-inset-0 -tw-z-10", {
  variants: {
    variant: {
      outline: "tw-ring-1 tw-ring-inset tw-ring-white/60",
      elevated: "tw-bg-white tw-shadow-[0_0_0_0.9px_rgba(0,0,0,0.07),0_2px_4px_0_rgba(0,0,0,0.08)] tw-overflow-hidden",
      cream: "tw-bg-cream tw-overflow-hidden",
      gray: "tw-bg-gray-200 tw-overflow-hidden",
      glass: "tw-overflow-hidden tw-border tw-border-solid tw-border-white/50 tw-bg-white/1 tw-shadow-none",
    },
    rounded: {
      default: "tw-rounded-tl-3xl tw-rounded-tr-lg tw-rounded-bl-3xl tw-rounded-br-3xl",
      sm: "tw-rounded-[1.25rem]",
      pill: "tw-rounded-full",
    },
  },
  defaultVariants: { variant: "outline", rounded: "default" },
});

export const userStaticBubbleVariants = cva(
  "tw-max-w-60 tw-w-fit tw-ml-auto tw-py-2.5 tw-px-3.5 tw-text-[0.875rem]/5 tw-font-medium",
  {
    variants: {
      variant: {
        outline: "tw-text-white tw-ring-1 tw-ring-inset tw-ring-white/60",
        elevated:
          "tw-text-black tw-bg-white tw-shadow-[0_0_0_0.9px_rgba(0,0,0,0.07),0_2px_4px_0_rgba(0,0,0,0.08)] tw-overflow-hidden",
        cream: "tw-text-black tw-bg-cream tw-overflow-hidden",
        gray: "tw-text-gray-500 tw-bg-gray-200 tw-overflow-hidden",
        glass:
          "tw-text-white tw-border tw-border-solid tw-border-white/50 tw-bg-white/1 tw-overflow-hidden tw-shadow-none",
      },
      rounded: {
        default: "tw-rounded-tl-3xl tw-rounded-tr-lg tw-rounded-bl-3xl tw-rounded-br-3xl",
        sm: "tw-rounded-[1.25rem]",
        pill: "tw-rounded-full",
      },
    },
    defaultVariants: { variant: "outline", rounded: "default" },
  },
);

export type UserStaticBubbleVariantProps = VariantProps<typeof userStaticBubbleVariants>;

// --- Shared ---

const BUBBLE_SHADOW = "tw-shadow-[0_0_0_0.5px_rgb(0_0_0/0.08),0_1.677px_3.354px_0_rgb(0_0_0/0.04)]";

// The reason for this is that adding these values through classes causes motion animations to generate artifacts. So
// use this when animating the buble's
export const assistantElevatedBubbleInlineStyle = {
  boxShadow: "0 0 0 0.5px rgb(0 0 0 / 0.08), 0 1.677px 3.354px 0 rgb(0 0 0 / 0.04)",
  borderRadius: 20,
} as const;

// --- Assistant bubble (split) ---

export const assistantBubbleRootVariants = cva("tw-relative tw-isolate tw-w-fit tw-max-w-full tw-text-[0.875rem]/5", {
  variants: {
    variant: {
      elevated: "tw-text-black",
      cream: "tw-text-black",
      ghost: "tw-text-black",
      glass: "tw-text-white",
    },
    rounded: {
      default:
        "tw-rounded-tl-[0.45rem] tw-rounded-tr-[1.35rem] tw-rounded-bl-[1.35rem] tw-rounded-br-[1.35rem] tw-py-2.5 tw-px-3.5",
      sm: "tw-rounded-[1.25rem] tw-py-2.5 tw-px-3.5",
      pill: "tw-items-center tw-h-10 tw-flex tw-overflow-hidden tw-rounded-full",
    },
  },
  defaultVariants: { variant: "elevated", rounded: "default" },
});

export const assistantBubbleBackgroundVariants = cva("tw-absolute tw-inset-0 -tw-z-10", {
  variants: {
    variant: {
      elevated: `tw-bg-white ${BUBBLE_SHADOW}`,
      cream: "tw-bg-cream",
      ghost: "tw-bg-transparent",
      glass: "tw-bg-black/15 tw-overflow-hidden tw-shadow-none",
    },
    rounded: {
      default: "tw-rounded-tl-[0.45rem] tw-rounded-tr-[1.35rem] tw-rounded-bl-[1.35rem] tw-rounded-br-[1.35rem]",
      sm: "tw-rounded-[1.25rem]",
      pill: "tw-rounded-full",
    },
  },
  defaultVariants: { variant: "elevated", rounded: "default" },
});

export const assistantStaticBubbleVariants = cva("tw-w-fit tw-max-w-full tw-text-[0.875rem]/5", {
  variants: {
    variant: {
      elevated: `tw-text-black tw-bg-white ${BUBBLE_SHADOW}`,
      cream: "tw-text-black tw-bg-cream",
      ghost: "tw-text-black tw-bg-transparent",
      glass: "tw-text-white tw-bg-black/15 tw-overflow-hidden tw-shadow-none",
    },
    rounded: {
      default:
        "tw-rounded-tl-[0.45rem] tw-rounded-tr-[1.35rem] tw-rounded-bl-[1.35rem] tw-rounded-br-[1.35rem] tw-py-2.5 tw-px-3.5",
      sm: "tw-rounded-[1.25rem] tw-py-2.5 tw-px-3.5",
      pill: "tw-items-center tw-h-10 tw-flex tw-overflow-hidden tw-rounded-full",
    },
  },
  defaultVariants: { variant: "elevated", rounded: "default" },
});

export type AssistantStaticBubbleVariantProps = VariantProps<typeof assistantStaticBubbleVariants>;
